import { Divider, IconFont, Input, pointer, Popover, Text } from '@byecode/ui'
import type { BreakPoint, DevicePreviewEnum } from '@lighthouse/core'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { useMainBreakPoint } from '@/hooks/useApplication'

import * as CM from '../styles'
import * as SC from './styles'

const SCxWrapper = styled.div`
    width: 100%;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const SCxList = styled.div`
    max-height: 180px;
    overflow: auto;
`

const SCxIconWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`

const SCxMainBreakPoint = styled(IconFont)`
    color: var(--color-main);
    font-size: 12px;
    position: absolute;
    right: -6px;
    top: -4px;
`

const SCxInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    overflow: hidden;
    height: 100%;
`

const SCxTick = styled(IconFont)``

const SCxOperator = styled.div`
    display: none;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
`

const SCxExtra = styled.div`
    width: 40px;
    display: flex;
    justify-content: flex-end;
`

const SCxListItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 36px;
    padding: 0 16px;
    ${pointer}

    &:hover {
        background-color: var(--color-gray-100);
        ${SCxTick} {
            display: none;
        }

        ${SCxOperator} {
            display: flex;
        }
    }
`

const DeviceTextMap: Record<DevicePreviewEnum, string> = {
    DESKTOP: 'PC端',
    MOBILE: '移动端'
}

interface PreviewPopoverProps {
    value: string
    list: BreakPoint[]
    hoverColor?: string
    onCreate: () => void
    onUpdate: (data: BreakPoint) => void
    onDelete: (id: BreakPoint) => void
    onChange: (id: string) => void
}

export const PreviewPopover: React.FC<PreviewPopoverProps> = ({ value, list, hoverColor, onCreate, onUpdate, onDelete, onChange }) => {
    const [open, setOpen] = useState(false)
    const mainBreakPoint = useMainBreakPoint()
    const handleUpdate = useCallback(
        (ev: React.MouseEvent<HTMLElement>, val: BreakPoint) => {
            ev.stopPropagation()
            onUpdate(val)
            setOpen(false)
        },
        [onUpdate]
    )

    const handleCreate = useCallback(() => {
        onCreate()
        setOpen(false)
    }, [onCreate])

    const handleDelete = useCallback(
        (ev: React.MouseEvent<HTMLElement>, val: BreakPoint) => {
            ev.stopPropagation()
            onDelete(val)
        },
        [onDelete]
    )

    const handleSelect = useCallback(
        (val: string) => {
            setOpen(false)
            onChange(val)
        },
        [onChange]
    )

    return (
        <Popover width={257} withinPortal opened={open} onChange={setOpen}>
            <Popover.Target>
                <SC.IconWrapper className={open ? 'open' : undefined} hoverColor={hoverColor}>
                    <CM.Icon fill="var(--color-gray-500)" type="ArrowDownSmall" size={18} />
                </SC.IconWrapper>
            </Popover.Target>
            <Popover.Dropdown>
                <SCxWrapper>
                    <SCxList>
                        {list.map(item => (
                            <SCxListItem key={item.id} onClick={() => handleSelect(item.id)}>
                                <SCxInfo>
                                    <SCxIconWrapper>
                                        {mainBreakPoint === item.id && <SCxMainBreakPoint type="Asterisk" size={8} />}
                                        <IconFont type={item.icon} size={16} fill="var(--color-gray-400)" />
                                    </SCxIconWrapper>
                                    <Text size={14}>{item.name}</Text>
                                    <Text size={14} style={{ overflow: 'visible' }} color="var(--color-gray-400)">
                                        {item.width}
                                    </Text>
                                </SCxInfo>
                                <SCxExtra>
                                    {value === item.id && <SCxTick type="Tick" size={16} fill="var(--color-main)" />}
                                    <SCxOperator>
                                        <IconFont type="edit" fill="var(--color-gray-400)" onClick={ev => handleUpdate(ev, item)} />
                                        {mainBreakPoint !== item.id && (
                                            <IconFont type="Trash" fill="var(--color-gray-400)" onClick={ev => handleDelete(ev, item)} />
                                        )}
                                    </SCxOperator>
                                </SCxExtra>
                            </SCxListItem>
                        ))}
                    </SCxList>
                    {list.length <= 4 && (
                        <>
                            <Divider />
                            <SCxListItem onClick={handleCreate}>
                                <SCxInfo>
                                    <IconFont type="Add2" size={16} fill="var(--color-gray-400)" />
                                    <Text size={14}>添加断点</Text>
                                </SCxInfo>
                            </SCxListItem>
                        </>
                    )}
                </SCxWrapper>
            </Popover.Dropdown>
        </Popover>
    )
}
